// ISDP CUSTOM: DevHub role model — single source of truth shared by the auth
// module (sign-in resolver), the permission policy, the MCP/PAT plugin, and the
// frontend (role guard + content gate). Roles are managed in the database
// (role_assignments table, keyed by email) via the admin Access Management UI.
//
// The four roles and what they can see:
//   - public  : ONLY the public marketing portal (public product tabs). Hard-blocked
//               from the internal Backstage app. No premium/registered content.
//   - partner : Stays on the public partner portal; premium content is unlocked per
//               product, for the products an admin assigned them. Hard-blocked from
//               the internal app.
//   - draeger : Any @draeger.com employee. Full internal developer hub AND all
//               partner/product content.
//   - admin   : Everything, plus the admin management surfaces.
//
// Which PRODUCTS exist is config (backend `partnerDocs`), not code. This library is
// shared with the frontend and stays config-free: it derives entitlements from the
// `premium-<id>` groups alone and leaves "is this a configured product?" to the backend.
import {
  premiumGroup,
  productIdsFromGroups,
  TIER_GROUPS,
  type ProductId,
} from './permissions';

export type DevHubRole = 'public' | 'partner' | 'draeger' | 'admin';

export const DEVHUB_ROLES: readonly DevHubRole[] = [
  'public',
  'partner',
  'draeger',
  'admin',
] as const;

export function isDevHubRole(value: unknown): value is DevHubRole {
  return (
    typeof value === 'string' && DEVHUB_ROLES.includes(value as DevHubRole)
  );
}

// Group reference constants (the values baked into the JWT `ent` claim).
export const GROUP_ADMINS = 'group:default/admins';
export const GROUP_INTERNAL = 'group:default/draeger-internal';
export const GROUP_EXTERNAL = 'group:default/external';
export const GROUP_REGISTERED = TIER_GROUPS.registered; // group:default/external-registered
// Marker that distinguishes a logged-in "public" user (no entitlements) from a
// "partner" (registered + premium). Without it, an external user with no premium
// groups is ambiguous; the marker makes the distinction explicit.
export const GROUP_PUBLIC = 'group:default/external-public';

/**
 * The set of group refs a role maps to. These are issued into the JWT at sign-in
 * so that the existing group-based checks (permission policy, nav visibility)
 * keep working unchanged.
 *
 * Premium is PER-PRODUCT for partners: `products` lists the product ids a partner
 * receives a premium group for (the sign-in resolver passes the ids stored for them).
 * Omitted or `[]` means a registered-only partner. There is deliberately no "all
 * products" default — the product list is config, which this library cannot see.
 * admin/draeger/public ignore `products`.
 */
export function groupsForRole(
  role: DevHubRole,
  products: readonly ProductId[] = [],
): string[] {
  switch (role) {
    case 'admin':
      return [GROUP_ADMINS, GROUP_INTERNAL];
    case 'draeger':
      return [GROUP_INTERNAL];
    case 'partner': {
      const premium = products.map(p => premiumGroup(p));
      return [GROUP_EXTERNAL, GROUP_REGISTERED, ...premium];
    }
    case 'public':
    default:
      return [GROUP_EXTERNAL, GROUP_PUBLIC];
  }
}

/**
 * Derive the effective role from a set of group refs (the inverse of
 * groupsForRole, used by the frontend and APIs which only have the JWT groups).
 */
export function roleFromGroups(groups: readonly string[]): DevHubRole {
  const set = new Set(groups);
  if (set.has(GROUP_ADMINS)) return 'admin';
  if (set.has(GROUP_INTERNAL)) return 'draeger';
  if (set.has(GROUP_EXTERNAL)) {
    // Explicit public marker, or no entitlements at all → public.
    if (set.has(GROUP_PUBLIC)) return 'public';
    const hasPremium = productIdsFromGroups(groups).length > 0;
    if (hasPremium || set.has(GROUP_REGISTERED)) return 'partner';
    return 'public';
  }
  return 'public';
}

/**
 * The product ids whose premium/API content this set of groups may access.
 *
 * A partner gets exactly the ids carried as `premium-<id>` groups. Internal roles
 * (admin/draeger) are entitled to EVERY product — but which products exist is config,
 * so callers that know it pass `allProductIds` (the backend passes the `partnerDocs`
 * keys); without it an internal caller gets `[]` and the caller decides by role, as
 * levelFor does. Ids are checked for shape here; whether a partner's id is a configured
 * product is checked where the config lives.
 */
export function entitledProducts(
  groups: readonly string[],
  allProductIds: readonly ProductId[] = [],
): ProductId[] {
  const role = roleFromGroups(groups);
  if (role === 'admin' || role === 'draeger') {
    return [...allProductIds];
  }
  return productIdsFromGroups(groups);
}

/**
 * CLIENT-SIDE ONLY: the entitlement list of a simulated or previewed partner face (the
 * localhost dev simulator, an internal "preview as partner"), meaning "every product".
 * It is never stored or issued in a token — PRODUCT_ID_PATTERN rejects it, so the backend
 * drops it — and the server enforces access regardless; it only drives presentation.
 */
export const ALL_PRODUCTS_PREVIEW = '*';

/**
 * The request header an internal reviewer's "view as" preview travels in, so the public
 * portal endpoints answer as the previewed face (anonymous | public | registered |
 * partner:<ids>). The backend honours it only for a real internal caller and only ever
 * to LOWER access — see callerViewer in the backend's authHelpers.
 */
export const PREVIEW_HEADER = 'x-devhub-preview';

/** Whether an entitlement list covers `productId` (an id match, or the preview wildcard). */
export function isEntitledTo(
  products: readonly string[],
  productId: string,
): boolean {
  return (
    products.includes(productId) || products.includes(ALL_PRODUCTS_PREVIEW)
  );
}

/** Only draeger employees and admins may enter the internal Backstage app. */
export function canAccessInternalApp(role: DevHubRole): boolean {
  return role === 'draeger' || role === 'admin';
}

/**
 * Who may mint Personal Access Tokens for the MCP server: draeger devs and admins
 * (entitled to every product), and partners with at least one entitled product.
 * Pure-public users have nothing to grant a token access to.
 */
export function canIssueTokens(groups: readonly string[]): boolean {
  const role = roleFromGroups(groups);
  if (role === 'admin' || role === 'draeger') return true;
  return productIdsFromGroups(groups).length > 0;
}

/** Human-friendly role label for UI. */
export const ROLE_LABELS: Record<DevHubRole, string> = {
  public: 'Public',
  partner: 'Registered partner',
  draeger: 'Dräger Dev',
  admin: 'Admin',
};

/** Short description of each role for the admin UI. */
export const ROLE_DESCRIPTIONS: Record<DevHubRole, string> = {
  public: 'Marketing portal only — no internal access, no unlocked product tabs.',
  partner:
    'Registered partner on the partner portal, with Premium (full documentation) unlocked per product — only for the products assigned to them.',
  draeger: 'Full internal developer hub plus all partner/product content.',
  admin: 'Full access including admin management surfaces.',
};
